/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import {
  Badge,
  BaseButton,
  Button,
  Grid,
  Icon,
  IconButton,
  List,
  Modal,
  Spacer,
  Spinner,
  TextGroupLarge,
  TextGroupMedium,
} from "@cd2/components"
import { Fields } from "@cd2/forms"
import { H3, H4, P } from "@cd2/text"
import { formatDate, pluralize } from "@cd2/tools"
import { titleize } from "@cd2/tools/build/es6"
import {
  add,
  endOfDay,
  endOfWeek,
  format,
  isLastDayOfMonth,
  setDate,
  setDay,
  startOfDay,
  startOfWeek,
} from "date-fns"
import React, { useEffect, useLayoutEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { LinkButton } from "../Components/Buttons/LinkButton"
import { ButtonsWrapper } from "../Components/ButtonsWrapper"
import { brandColors, themeColors } from "../Components/EOSThemeProvider"
import { useGlobalState } from "../Components/GlobalStateProvider"
import { InteractivePopout } from "../Components/InteractivePopout"
import { KeyValueList } from "../Components/KeyValueList"
import { FloatingBox } from "../Components/Misc/FloatingBox"
import { PageHelmet } from "../Components/PageHelmet"
import { QuickToolTip } from "../Components/QuickTooltip"
import { LeadsSelect } from "../Components/SelectLists/LeadsSelectList"
import { MultiUserSelectList } from "../Components/SelectLists/MultiUserSelectList"
import { OrdersSelectList } from "../Components/SelectLists/OrdersSelectList"
import { ProjectsSelectList } from "../Components/SelectLists/ProjectsSelectList"
import { QuotesSelectList } from "../Components/SelectLists/QuotesSelectList"
import { SalesRepSelectList } from "../Components/SelectLists/SalesRepSelectList"
import { APP_LEVEL_TASK_BAR } from "../Components/SiteWrapper"
import { icons } from "../constants/icons"
import { useAsyncCallback } from "../hooks/useAsyncCallback"
import { useAsyncEffect } from "../hooks/useAsyncEffect"
import { useInterval } from "../hooks/useInterval"
import { useSiteAlerts } from "../hooks/useSiteAlerts"
import { HoverCustomerLoader } from "../hoverLoaders/HoverCustomerLoader"
import { HoverLeadLoader } from "../hoverLoaders/HoverLeadsLoader"
import { HoverOrderLoader } from "../hoverLoaders/HoverOrderLoader"
import { HoverProjectLoader } from "../hoverLoaders/HoverProjectLoader"
import { HoverQuoteLoader } from "../hoverLoaders/HoverQuoteLoader"
import { HoverSalesRepLoader } from "../hoverLoaders/HoverSalesRepLoader"
import { eRoutes } from "../routes"
import { IRecord } from "../types/schema"
import { useSettings } from "../utils/useSettings"
import { CustomerSelectList2 } from "../Components/SelectLists/CustomerSelectList2"
import { buildCustomerSearchFilter } from "../../shared/search"

// TODO: reminder trigger emails
// TODO: overdue trigger emails
// TODO: Generate repeat task on complete of previous task
// TODO: Error handling on create / edit fails
// TODO: Create a card to display reminders on resources
// TODO: Create interface to create a reminder on a quote
// TODO: Create interface to create a reminder on an order
// TODO: Display the resource card
// TODO: Improve responsiveness of the page
// TODO: Import the responsiveness of the new form
// TODO: Stop the tasks pop up popping up on the task page
// TODO: Add sorting to the page
// TODO: On the preview pane - have a cancel button and a complete check rather than a dropdown
// TODO: Hover action on the complete circle
// TODO: Click on a check mark to uncomplete a task
// TODO: A way to close the Create task pop up
// TODO: Have a link on a task preview to go to the tasks page with that task focused
// TODO: Emphasise when a task is assigned to you
// TODO: if a task is overdue - color red
// TODO: Dont have closing a task trigger the popup
// TODO: Close the focused sidebar when you click on the add task

enum tabs {
  all = "All tasks",
  createdByMe = "Created by me",
  AssignedToMe = "Assigned to me",
  ThisWeek = "Due this week",
}

enum taskStatuses {
  open = "open",
  done = "done",
  cancelled = "cancelled",
}

enum frequency {
  daily = "daily",
  weekly = "weekly",
  monthly = "monthly",
}

enum resources {
  customers = "customers",
  leads = "leads",
  quotes = "quotes",
  orders = "orders",
  projects = "projects",
  sales_reps = "sales_reps",
}

type ITaskResourceModels = "customers" | "leads" | "projects" | "quotes" | "sales_reps" | "orders"

export function TasksPage() {
  const [tab, setTab] = useState<tabs>(tabs.all)
  const { currentUser } = useGlobalState()
  const settings = useSettings()

  const filters = () => {
    if (tab === tabs.all) {
      return {
        or: [
          { matches: { assigned_to: { some: { id: currentUser.id } } } },
          { matches: { created_by: currentUser.id } },
          settings.viewAllBranchTasksInTheAllTasksView && {
            matches: { branch: currentUser.branch.id },
          },
        ].filter(Boolean),
      }
    } else if (tab === tabs.createdByMe) {
      return { matches: { created_by: currentUser.id } }
    } else if (tab === tabs.AssignedToMe) {
      return { matches: { assigned_to: { some: { id: currentUser.id } } } }
    } else if (tab === tabs.ThisWeek) {
      return {
        and: [
          {
            or: [
              { matches: { assigned_to: { some: { id: currentUser.id } } } },
              { matches: { created_by: currentUser.id } },
            ],
          },
          {
            and: [
              {
                matches: {
                  due_at: { gte: startOfWeek(new Date()) },
                },
              },
              {
                matches: {
                  due_at: { lte: endOfWeek(new Date()) },
                },
              },
            ],
          },
        ],
      }
    }
  }

  return (
    <div>
      <PageHelmet>Tasks</PageHelmet>
      <div
        css={css`
          padding: 32px 32px 0;
        `}
      >
        <TextGroupLarge
          heading="Tasks"
          body="Tasks can be set against a number of resources and will trigger emails and notifications to remind you when they're due."
        />

        <Spacer size={32} />
        <div
          css={css`
            display: flex;
            gap: 32px;
            border-bottom: 1px solid #ddd;
          `}
        >
          {Object.keys(tabs).map(internalTabKey => {
            const internalTab = tabs[internalTabKey as keyof typeof tabs]
            return (
              <div key={internalTabKey}>
                <div
                  onClick={() => {
                    setTab(internalTab)
                  }}
                  css={css`
                    opacity: ${internalTab === tab ? 1 : 0.5};
                    border-bottom: 2px solid ${internalTab === tab ? brandColors.darkMint : "white"};
                    margin-bottom: -1px;
                    padding-bottom: 8px;
                    cursor: pointer;
                    width: max-content;
                  `}
                >
                  {internalTab}
                </div>
              </div>
            )
          })}
        </div>
      </div>
      <TaskList filter={filters()} />
    </div>
  )
}

function NewTaskForm({
  afterCreate,
  model: initialModel,
  recordId: initialRecordId,
  autofocus = false,
  onCancel,
}: {
  afterCreate(): Promise<void> | void
  model?: string
  recordId?: string

  autofocus?: boolean
  onCancel?(): void
}) {
  const skeem = window.skeem
  const alerts = useSiteAlerts()
  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  const { currentUser } = useGlobalState()

  const [isCreating, setIsCreating] = useState(autofocus)
  const [body, setBody] = useState("")
  const [dueDate, setDueDate] = useState<Date | null>(null)
  const [errors, setErrors] = useState<{ body?: string }>({})
  const [repeatFrequency, setRepeatFrequency] = useState<IRepeatFrequency | undefined>()
  const [repeatSchedule, setRepeatSchedule] = useState<IRepeatSchedule>(null)
  const [model, setModel] = useState<resources | null>((initialModel as resources) ?? null)
  const [recordId, setRecordId] = useState<string | null>(initialRecordId ?? null)
  const [assignedUsers, setAssignedUsers] = useState<
    { name: string; id: string; skeem_email: string }[] | null
  >(null)
  const addTaskText = "Add a task..."

  useEffect(() => {
    if (textAreaRef.current) {
      textAreaRef.current.style.height = textAreaRef.current.scrollHeight + "px"
    }
  }, [body])

  const handleCreate = useAsyncCallback(async () => {
    if (body.length === 0) {
      setErrors({ body: "Please enter a task" })
      return
    }
    try {
      await skeem.mutate({
        tasks: {
          create: removeNulls({
            body,
            due_at: dueDate,
            created_by: { set: currentUser.id },
            assigned_to:
              assignedUsers?.length ?? 0 > 0 ? { set: assignedUsers?.map(u => u.id) } : undefined,
            status: taskStatuses.open,
            repeat_frequency: repeatFrequency,
            repeat_schedule: repeatSchedule,
            model,
            record_id: recordId,
            branch: { set: currentUser.branch.id },
          }),
        },
      })
      await afterCreate()
      alerts.genericSuccess("Task created")
      setIsCreating(false)
      setBody("")
      setDueDate(null)
      setErrors({})
      setRepeatFrequency(undefined)
      setRepeatSchedule(null)
      setModel(null)
      setRecordId(null)
      setAssignedUsers(null)
    } catch (e) {
      alerts.genericError(e.message)
    }
  })

  return (
    <FloatingBox
      css={css(
        taskCardStyles(),
        css`
          ${FloatingBox.vars.childPadding}: 0;
        `,
      )}
      onClick={isCreating ? undefined : () => setIsCreating(true)}
    >
      <Grid
        css={css`
          padding: 16px;
          border-bottom: 1px solid ${isCreating ? "#ddd" : "transparent"};
          background: white;
          border-radius: 4px;
        `}
        columns={["max-content", { fr: 1 }]}
        alignItems="start"
        gap={16}
      >
        <div>
          <Icon icon={icons.plus} />
        </div>
        <div>
          {isCreating ? (
            <textarea
              ref={textAreaRef}
              css={css`
                padding: 0;
                -webkit-appearance: none;
                border: none;
                display: block;
                outline: none;
                background: transparent;
                width: 100%;
                resize: none;
                overflow: hidden;

                &::placeholder {
                  color: ${errors.body ? "red" : "#aaa"};
                }
              `}
              value={body}
              onChange={e => setBody(e.target.value)}
              placeholder={addTaskText}
              autoFocus
            />
          ) : (
            <div
              css={css`
                height: 32px;
              `}
            >
              {addTaskText}
            </div>
          )}
        </div>
      </Grid>
      <div
        css={css`
          height: ${isCreating ? 40 : 0}px;
          overflow: ${isCreating ? "normal" : "hidden"};
          transition: 0.3s;
          background-color: #f1f1f1;
          border-radius: 0 0 4px 4px;
          padding: ${isCreating ? 4 : 0}px 8px;
        `}
      >
        <Grid
          columns={
            [
              "max-content",
              "max-content",
              "max-content",
              !model && "max-content",
              { fr: 1 },
              "max-content",
              onCancel && "max-content",
            ].filter(Boolean) as any
          }
          gap={4}
          alignItems="center"
        >
          <DueDatePopout dueAt={dueDate} onChange={setDueDate} minDate={new Date()} compact />
          <AssignUsersPopout users={assignedUsers} onChange={setAssignedUsers} compact />
          <RepeatTaskPopout
            repeatFrequency={repeatFrequency}
            repeatSchedule={repeatSchedule}
            onChange={opts => {
              setRepeatFrequency(opts.repeatFrequency)
              setRepeatSchedule(opts.repeatSchedule)
            }}
            compact
          />
          {!initialModel && (
            <ResourcePopout
              model={model as resources}
              recordId={recordId ?? ""}
              onChange={opts => {
                setModel(opts.model)
                setRecordId(opts.recordId)
              }}
              compact
            />
          )}
          <div />
          <BaseButton
            {...smallControlButtonProps}
            renderLeft={() => <Icon icon={icons.save} />}
            onClick={handleCreate}
            disabled={handleCreate.running || body.length === 0}
          >
            Save
          </BaseButton>
          {onCancel && (
            <BaseButton
              {...smallControlButtonProps}
              onClick={onCancel}
              disabled={handleCreate.running}
            >
              Cancel
            </BaseButton>
          )}
        </Grid>
      </div>
    </FloatingBox>
  )
}

const basicTaskAttrs = [
  "body",
  "due_at",
  "status",
  "repeat_frequency",
  "repeat_schedule",
  "model",
  "record_id",
  "created_at",
  "created_by",
  "assigned_to",
] as const
const basicAssignedToAttrs = ["name", "skeem_email"] as const
type IUser = IRecord<"skeem_users", (typeof basicAssignedToAttrs)[number]>
type ITask = IRecord<"tasks", (typeof basicTaskAttrs)[number]> & {
  assigned_to: IUser[]
}

function TaskList({ filter }: { filter?: any }) {
  const skeem = window.skeem

  const containerRef = useRef<HTMLDivElement>(null)
  const [tasks, setTasks] = useState<ITask[] | null>(null)
  const [focusedTaskId, setFocusedTaskId] = useState<string | null>(null)
  const [displayHistory, setDisplayHistory] = useState(false)

  const [areaHeight, setAreaHeight] = useState(0)

  useAsyncEffect(async () => {
    await fetchTasks()
  }, [JSON.stringify(filter)])

  useLayoutEffect(() => {
    if (!containerRef.current) {
      return
    }
    // Get elements distance from top of page
    const offset = containerRef.current.getBoundingClientRect().top ?? 0
    // Get page height
    const pageHeight = document.documentElement.clientHeight
    // set element height to be minimum of the remaining page height
    const height = pageHeight - offset
    setAreaHeight(height)
  }, [])

  async function fetchTasks() {
    const tasks = await skeem.fetch2<ITask[]>({
      model: {
        name: "tasks",
        attributes: [...basicTaskAttrs, { name: "assigned_to", attributes: basicAssignedToAttrs }],
        filter,
        sort: { by: "due_at", direction: "asc" },
      },
    })
    setTasks(tasks)
  }

  function handleUpdateSingleRecord(data: ITask) {
    setTasks(tasks => {
      if (!tasks) {
        return null
      }
      return tasks?.map(task => {
        if (task.id === data.id) {
          return data
        }
        return task
      })
    })
  }

  const activeTasks = tasks?.filter(task => task.status === taskStatuses.open) ?? []
  const inactiveTasks = tasks?.filter(task => task.status !== taskStatuses.open) ?? []

  return (
    <div
      ref={containerRef}
      css={css`
        background-color: ${paneBackground};
      `}
    >
      <Grid columns={[{ fr: 1 }, 360]} css={css``}>
        <List
          css={css`
            padding: 16px 32px 16px 16px;
            height: ${areaHeight}px;
            overflow: auto;
          `}
          size={16}
        >
          <NewTaskForm afterCreate={fetchTasks} />
          {activeTasks?.map(task => (
            <TaskListItem
              key={task.id}
              onClick={() => setFocusedTaskId(task.id)}
              isActive={task.id === focusedTaskId}
              task={task}
              onUpdate={handleUpdateSingleRecord}
            />
          ))}
          <div
            css={css`
              padding: 16px;
              cursor: pointer;
            `}
            onClick={() => setDisplayHistory(!displayHistory)}
          >
            <Icon icon={displayHistory ? icons.chevronDown : icons.chevronRight} /> Completed (
            {inactiveTasks.length})
          </div>
          {displayHistory &&
            inactiveTasks?.map(task => (
              <TaskListItem
                key={task.id}
                onClick={() => setFocusedTaskId(task.id)}
                isActive={task.id === focusedTaskId}
                task={task}
                onUpdate={handleUpdateSingleRecord}
              />
            ))}
        </List>
        {focusedTaskId && (
          <div>
            <div
              css={css`
                background-color: ${paneBackground};
                padding: 16px;
                box-shadow: -8px 0 5px -5px rgba(0, 0, 0, 0.1);
                height: ${areaHeight}px;
                overflow: auto;
              `}
            >
              <RenderTaskPane taskId={focusedTaskId} onUpdateData={handleUpdateSingleRecord} />
            </div>
          </div>
        )}
      </Grid>
    </div>
  )
}

export function TaskListItem({
  onClick,
  isActive,
  task,
  onUpdate,
  setParentCanClose,
  className,
  compact,
  popOutZIndex,
}: {
  onClick?: () => void
  isActive?: boolean
  task: ITask
  onUpdate: (data: ITask) => void
  setParentCanClose?: React.Dispatch<React.SetStateAction<boolean>>
  className?: string
  compact?: boolean
  popOutZIndex?: number
}) {
  const alert = useSiteAlerts()
  const skeem = window.skeem

  async function handleCheckTaskAsComplete(id: string) {
    await skeem.mutate({
      tasks: {
        update: {
          id,
          status: taskStatuses.done,
        },
      },
    })
    handleTaskCompletedRepeatable()
    onUpdate({ ...task, status: taskStatuses.done })
  }

  async function handleTaskCompletedRepeatable() {
    if (!task.repeat_frequency) {
      return
    }
    const taskData = {
      status: taskStatuses.open,
      body: task.body,
      model: task.model,
      record_id: task.record_id,
      repeat_frequency: task.repeat_frequency,
      repeat_schedule: task.repeat_schedule,
      created_by: { set: skeem.currentSession.id },
      assigned_to: { set: task.assigned_to.map(v => v.id) },
      due_at: new Date(),
    }

    const continueDate = task.due_at ? new Date(task.due_at) : new Date()
    const scheduleDate = (task.repeat_schedule as { numbers: number[] })?.numbers?.[0]
    if (task.repeat_frequency === frequency.daily) {
      taskData.due_at = add(continueDate, { days: 1 })
    } else if (task.repeat_frequency === frequency.weekly) {
      if (typeof scheduleDate !== "number") {
        taskData.due_at = add(continueDate, { weeks: 1 })
      } else {
        // date fns uses sunday as 0, re-align dates from 0-6
        const alignedDate = scheduleDate % 7
        const datePlus = add(continueDate, { weeks: 1 })
        taskData.due_at = setDay(datePlus, alignedDate)
      }
    } else if (task.repeat_frequency === frequency.monthly) {
      if (typeof scheduleDate !== "number") {
        taskData.due_at = add(continueDate, { months: 1 })
      } else {
        const datePlus = add(continueDate, { months: 1 })
        // adding a month exact will only add days up to end of month. e.g if 31st is selected, going
        // into feb, will set to 28th with {months: 1.}
        // check if it's last day of month and use it if it is.
        if (isLastDayOfMonth(datePlus)) {
          taskData.due_at = datePlus
        } else {
          taskData.due_at = setDate(datePlus, scheduleDate)
        }
      }
    } else {
      console.error("Invalid frequency supplied")
      alert.genericError("Error creating repeated task")
      return
    }
    try {
      await skeem.mutate({ tasks: { create: taskData } })
      alert.genericSuccess("Repeating task created")
    } catch (e) {
      console.error(e)
      alert.genericError("Error creating repeated task")
    }
  }

  async function handleSnooze(newDate: Date) {
    const newTask = await skeem.mutate<IRecord<"tasks", "due_at">>({
      tasks: {
        update: {
          id: task.id,
          due_at: newDate,
        },
      },
    })
    setParentCanClose?.(true)
    alert.genericSuccess(`Task snoozed until ${format(newDate, "dd/MM/yy")}`)
    onUpdate({ ...task, due_at: newTask.due_at })
  }

  const snoozeButton = (
    <div>
      {task.due_at && (
        <DueDatePopout
          key={task.due_at}
          dueAt={null}
          onChange={handleSnooze}
          onCalendarClose={() => setParentCanClose?.(true)}
          popOutZIndex={popOutZIndex}
          target={({ setIsOpen, isOpen }) => (
            <BaseButton
              {...smallControlButtonProps}
              onClick={() => {
                setIsOpen(!isOpen)
                // this state is reversed - is open is false when first clicking, so we want to make
                // parent not closeable clicking outside.
                setParentCanClose?.(isOpen)
              }}
              renderLeft={() => <Icon icon={icons.snooze} />}
              buttonStyle="text"
            >
              Snooze
            </BaseButton>
          )}
          minDate={add(new Date(task.due_at), { days: 1 })}
          compact
        />
      )}
    </div>
  )
  const isOverdue =
    task.due_at && task.status === taskStatuses.open ? new Date() > new Date(task.due_at) : false

  async function handleDeleteTask() {
    if (!task?.id) {
      return
    }
    await skeem.mutate({ tasks: { destroy: task.id } })
    onUpdate({ ...task, status: taskStatuses.cancelled })
    setParentCanClose?.(true)
  }

  const deleteButton =
    task.status === taskStatuses.cancelled ? null : (
      <InteractivePopout
        content={({ setIsOpen }) => (
          <div>
            <Grid columns={["max-content", "max-content"]} gap={8}>
              <BaseButton
                renderLeft={() => <Icon icon={icons.cross} />}
                {...smallControlButtonProps}
                onClick={event => {
                  event.preventDefault()
                  event.stopPropagation()
                  setIsOpen(false)
                }}
              >
                Cancel
              </BaseButton>
              <BaseButton
                renderLeft={() => <Icon icon={icons.delete} />}
                {...smallControlButtonProps}
                onClick={event => {
                  event.preventDefault()
                  event.stopPropagation()
                  handleDeleteTask()
                  setIsOpen(false)
                }}
              >
                Delete
              </BaseButton>
            </Grid>
          </div>
        )}
        onClickOutside={() => setParentCanClose?.(true)}
        popOutZIndex={popOutZIndex}
      >
        {({ setIsOpen, isOpen }) => {
          function handleOpen(e: any) {
            e.preventDefault()
            e.stopPropagation()
            setParentCanClose?.(isOpen)
            setIsOpen(!isOpen)
          }

          return compact ? (
            <BaseButton {...smallControlButtonProps} onClick={handleOpen}>
              <Icon icon={icons.delete} />
            </BaseButton>
          ) : (
            <BaseButton
              renderLeft={() => <Icon icon={icons.delete} />}
              {...smallControlButtonProps}
              onClick={handleOpen}
            >
              Delete
            </BaseButton>
          )
        }}
      </InteractivePopout>
    )

  return (
    <FloatingBox
      css={css(
        taskCardStyles(isActive, isOverdue),
        css`
          cursor: ${onClick ? "pointer" : "default"};
        `,
      )}
      className={className}
      onClick={
        onClick
          ? event => {
              event.preventDefault()
              event.stopPropagation()
              onClick()
            }
          : undefined
      }
    >
      <Grid columns={["max-content", { fr: 1 }, "max-content"]} alignItems="center" gap={16}>
        <div>
          {task.status === taskStatuses.done ? (
            <Icon icon={icons.check} />
          ) : (
            <div
              css={css`
                height: 20px;
                width: 20px;
                border-radius: 50%;
                border: 2px solid black;
                cursor: pointer;
              `}
              onClick={e => {
                e.stopPropagation()
                handleCheckTaskAsComplete(task.id)
              }}
            />
          )}
        </div>
        <div>
          <div
            css={css`
              font-size: 15px;
              margin-bottom: 6px;
            `}
          >
            {task.body}
          </div>
          <div
            css={css`
              font-size: 12px;
              color: #666;
            `}
          >
            <div
              css={css`
                flex-basis: max-content;
                display: flex;
                flex-wrap: wrap;
                gap: 12px;
              `}
            >
              {task.due_at && (
                <span>
                  <Icon icon={icons.calendar} /> Due: {formatDate(task.due_at)}
                </span>
              )}
              {task.assigned_to?.length > 0 && (
                <span>
                  <Icon icon={icons.users} /> Assigned to:{" "}
                  {nameList(
                    task.assigned_to.map(user => user.name),
                    3,
                  )}
                </span>
              )}
              {task.repeat_frequency && (
                <span>
                  <Icon icon={icons.refresh} />{" "}
                  {repeatScheduleToText({
                    repeatFrequency: task.repeat_frequency as any,
                    repeatSchedule: task.repeat_schedule as any,
                  })}
                </span>
              )}
              {task.model && (
                <span>
                  <Icon icon={icons.jobs} /> Resource: {titleize(task.model)}
                </span>
              )}
            </div>
          </div>
        </div>
        {!compact && (
          <Grid columns={["max-content", "max-content"]} gap={6} alignItems="center">
            <QuickToolTip
              color="white"
              content={
                <KeyValueList
                  data={[
                    {
                      label: "Created by",
                      value: {
                        hover: {
                          model: "user",
                          id: task.created_by?.id,
                        },
                      },
                    },
                    {
                      label: "Assigned to",
                      value:
                        task.assigned_to.length === 1
                          ? {
                              hover: {
                                model: "user",
                                id: task.assigned_to[0].id,
                              },
                            }
                          : pluralize(task.assigned_to.length, "User"),
                    },
                    {
                      label: "Created at",
                      value: {
                        date: task.created_at,
                      },
                    },
                  ]}
                />
              }
            >
              <Icon icon={icons.info} />
            </QuickToolTip>
            <Grid>
              {task.due_at && snoozeButton}
              {deleteButton}
            </Grid>
          </Grid>
        )}
      </Grid>
    </FloatingBox>
  )
}

function RenderTaskPane({
  taskId,
  onUpdateData,
}: {
  taskId: string
  onUpdateData(data: ITask): void
}) {
  const skeem = window.skeem
  const [task, setTask] = useState<ITask | null>(null)
  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  const debounceRef = useRef<any>(null)

  function handleChange(data: any) {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }
    const newData = { ...task, ...data }
    setTask(task => ({ ...task, ...data }))
    debounceRef.current = setTimeout(() => {
      console.log("updating", newData)
      handleSave(newData)
    }, 300)
  }

  // save after there is a 1 second pause in changing
  async function handleSave(data: ITask) {
    await skeem.mutate({
      tasks: {
        update: {
          id: data.id,
          body: data.body,
          due_at: data.due_at,
          status: data.status,
          repeat_frequency: data.repeat_frequency,
          repeat_schedule: data.repeat_schedule,
          assigned_to: { set: data.assigned_to.map(user => user.id) },
          model: data.model,
          record_id: data.record_id,
        },
      },
    })
    onUpdateData(data)
  }

  useAsyncEffect(async () => {
    const task = await skeem.fetch2<ITask>({
      first: {
        name: "tasks",
        attributes: [...basicTaskAttrs, { name: "assigned_to", attributes: basicAssignedToAttrs }],
        filter: { matches: { id: taskId } },
      },
    })
    setTask(task)
  }, [taskId])

  useEffect(() => {
    if (textAreaRef.current) {
      textAreaRef.current.style.height = textAreaRef.current.scrollHeight + "px"
    }
  }, [task?.body])

  if (!task) {
    return <Spinner />
  }

  return (
    <Grid gap={8}>
      <div
        css={css(
          taskCardStyles(),
          css`
            font-size: 16px;
            padding: 16px;
          `,
        )}
      >
        <textarea
          ref={textAreaRef}
          css={css`
            padding: 0;
            -webkit-appearance: none;
            border: none;
            display: block;
            outline: none;
            background: transparent;
            width: 100%;
            resize: none;
            overflow: hidden;
          `}
          value={task.body}
          onChange={e => {
            handleChange({ body: e.target.value })
          }}
        />
      </div>
      <DueDatePopout
        dueAt={task.due_at}
        onChange={value => handleChange({ due_at: value ? String(value) : null })}
        minDate={new Date()}
      />
      <AssignUsersPopout
        users={task.assigned_to}
        onChange={users => handleChange({ assigned_to: users })}
      />
      <RepeatTaskPopout
        repeatSchedule={task.repeat_schedule as any}
        repeatFrequency={task.repeat_frequency as frequency}
        onChange={value =>
          handleChange({
            repeat_frequency: value.repeatFrequency,
            repeat_schedule: value.repeatSchedule,
          })
        }
      />
      <ResourcePopout
        model={task.model as resources}
        recordId={task.record_id}
        onChange={opts =>
          handleChange({
            model: opts.model,
            record_id: opts.recordId,
          })
        }
      />
      {task.model === "customers" && <HoverCustomerLoader id={task.record_id} inline />}
      {task.model === "leads" && <HoverLeadLoader id={task.record_id} inline />}
      {task.model === "quotes" && <HoverQuoteLoader id={task.record_id} inline />}
      {task.model === "orders" && <HoverOrderLoader id={task.record_id} inline />}
      {task.model === "sales_reps" && <HoverSalesRepLoader id={task.record_id} inline />}
      {task.model === "projects" && <HoverProjectLoader id={task.record_id} inline />}
      <div>
        <Fields.Select2
          options={Object.keys(taskStatuses).map(statusKey => ({
            value: statusKey,
            label: titleize(statusKey),
          }))}
          value={{ value: task?.status, label: titleize(task?.status) }}
          onChange={opt => handleChange({ status: opt.value })}
        />
      </div>
    </Grid>
  )
}

function DueDatePopout({
  dueAt,
  onChange,
  compact,
  target,
  minDate,
  onCalendarClose,
  popOutZIndex,
}: {
  dueAt: string | Date | null
  onChange: (due_at: Date) => void
  compact?: boolean
  target?: (data: {
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
    isOpen: boolean
  }) => React.ReactNode
  minDate?: Date
  onCalendarClose?(): void
  popOutZIndex?: number
}) {
  function handleChange(date: Date) {
    onChange(date)
  }

  return (
    <div>
      <InteractivePopout
        content={() => (
          <Fields.Date2
            onChange={handleChange}
            value={dueAt ? new Date(dueAt) : null}
            minDate={minDate}
            inline
          />
        )}
        onClickOutside={onCalendarClose}
        popOutZIndex={popOutZIndex}
      >
        {target ??
          (({ setIsOpen, isOpen }) => (
            <BaseButton
              {...(compact ? smallControlButtonProps : controlButtonProps)}
              renderLeft={() => <Icon icon={icons.calendar} />}
              onClick={() => setIsOpen(!isOpen)}
            >
              {dueAt ? formatDate(dueAt) : "Due..."}
            </BaseButton>
          ))}
      </InteractivePopout>
    </div>
  )
}

function AssignUsersPopout({
  users,
  onChange,
  compact,
}: {
  users: IUser[] | null
  onChange: (users: IUser[]) => void
  compact?: boolean
}) {
  function handleChange(users: IUser[]) {
    onChange(users)
  }

  return (
    <div>
      <InteractivePopout
        content={() => (
          <div
            css={css`
              min-width: 200px;
            `}
          >
            <MultiUserSelectList value={users ?? []} onChange={handleChange} />
          </div>
        )}
      >
        {({ setIsOpen, isOpen }) => (
          <BaseButton
            {...(compact ? smallControlButtonProps : controlButtonProps)}
            renderLeft={() => <Icon icon={icons.users} />}
            onClick={() => setIsOpen(!isOpen)}
          >
            {users && users.length > 0
              ? nameList(
                  users.map(user => user.name),
                  compact ? 1 : 3,
                )
              : "Assign..."}
          </BaseButton>
        )}
      </InteractivePopout>
    </div>
  )
}

function ResourcePopout({
  model,
  recordId,
  onChange,
  compact,
}: {
  model: resources
  recordId: string
  onChange: (resourceOpts: { model: resources; recordId: string }) => void
  compact?: boolean
}) {
  const [localModel, setLocalModel] = useState<resources>(model)

  function handleChange(resourceOpts: { model: resources; recordId: string }) {
    onChange(resourceOpts)
  }

  return (
    <div>
      <InteractivePopout
        content={() => (
          <div
            css={css`
              min-width: 200px;
            `}
          >
            <Fields.Select2
              options={Object.keys(resources).map(modelKey => ({
                value: modelKey,
                label: titleize(modelKey),
              }))}
              value={localModel ? { value: localModel, label: titleize(localModel) } : null}
              onChange={opt => setLocalModel(opt.value as resources)}
            />
            {localModel === "customers" && (
              <CustomerSelectList2
                value={recordId}
                onChange={id => handleChange({ model: localModel, recordId: id || "" })}
                searchFilter={query => ({
                  or: [
                    buildCustomerSearchFilter(query),
                    {
                      exists: {
                        path: [
                          {
                            name: "addresses",
                            filter: {
                              and: [
                                {
                                  matches: { showInCustomerAddressOptions: true },
                                },
                                {
                                  like: [
                                    {
                                      stringReplace: [{ attr: "postcode" }, " ", ""],
                                    },
                                    { value: query.replace(/\s/g, "") },
                                  ],
                                },
                              ],
                            },
                          },
                        ],
                      },
                    },
                  ],
                })}
                includeBranchName
              />
            )}
            {localModel === "leads" && (
              <LeadsSelect
                value={recordId}
                onChange={id => handleChange({ model: localModel, recordId: id })}
              />
            )}
            {localModel === "projects" && (
              <ProjectsSelectList
                value={recordId}
                onChange={id => handleChange({ model: localModel, recordId: id })}
              />
            )}
            {localModel === "quotes" && (
              <QuotesSelectList
                value={recordId}
                onChange={id => handleChange({ model: localModel, recordId: id })}
              />
            )}
            {localModel === "orders" && (
              <OrdersSelectList
                value={recordId}
                onChange={id => handleChange({ model: localModel, recordId: id })}
              />
            )}
            {localModel === "sales_reps" && (
              <SalesRepSelectList
                value={recordId}
                onChange={id => handleChange({ model: localModel, recordId: id })}
              />
            )}
          </div>
        )}
      >
        {({ setIsOpen, isOpen }) => (
          <BaseButton
            {...(compact ? smallControlButtonProps : controlButtonProps)}
            renderLeft={() => <Icon icon={icons.jobs} />}
            onClick={() => setIsOpen(!isOpen)}
          >
            {model && recordId ? `Resource: ${titleize(model)}` : "Resource..."}
          </BaseButton>
        )}
      </InteractivePopout>
    </div>
  )
}

type IRepeatFrequency = frequency
type IDaysOfWeek =
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday"
  | "sunday"
type IRepeatSchedule = { numbers: number[] } | null

function RepeatTaskPopout({
  repeatFrequency,
  repeatSchedule,
  onChange,
  compact,
}: {
  repeatFrequency?: IRepeatFrequency
  repeatSchedule: IRepeatSchedule
  onChange: (repeatOption: {
    repeatFrequency: IRepeatFrequency
    repeatSchedule: IRepeatSchedule
  }) => void
  compact?: boolean
}) {
  function handleChange(repeatOption: {
    repeatFrequency: IRepeatFrequency
    repeatSchedule: IRepeatSchedule
  }) {
    onChange(repeatOption)
  }

  return (
    <div>
      <InteractivePopout
        content={() => (
          <div
            css={css`
              min-width: 200px;
            `}
          >
            <Fields.Select2
              options={Object.keys(frequency).map(freqKey => ({
                value: freqKey,
                label: titleize(freqKey),
              }))}
              value={
                repeatFrequency
                  ? { value: repeatFrequency, label: titleize(repeatFrequency) }
                  : null
              }
              onChange={opt => {
                let schedule = repeatSchedule
                if (repeatFrequency !== opt.value) {
                  if (opt.value === frequency.weekly) {
                    schedule = { numbers: [new Date().getDay()] }
                  }
                  if (opt.value === frequency.monthly) {
                    schedule = { numbers: [new Date().getDate()] }
                  }
                }
                handleChange({
                  repeatFrequency: opt.value as IRepeatFrequency,
                  repeatSchedule: schedule,
                })
              }}
            />
            {repeatFrequency === "weekly" && (
              <Fields.Select2
                options={arrayOfConsecutiveNumbers(1, 7).map(dayNumber => ({
                  value: dayNumber,
                  label: dayNumberToDayOfWeek(dayNumber),
                }))}
                value={
                  repeatSchedule
                    ? {
                        value: repeatSchedule.numbers[0],
                        label: dayNumberToDayOfWeek(repeatSchedule.numbers[0]),
                      }
                    : null
                }
                onChange={opt =>
                  handleChange({ repeatFrequency, repeatSchedule: { numbers: [opt.value] } })
                }
              />
            )}
            {repeatFrequency === "monthly" && (
              <Fields.Select2
                options={arrayOfConsecutiveNumbers(1, 31).map(dayNumber => ({
                  value: dayNumber,
                  label: numberToOrdinal(dayNumber),
                }))}
                value={
                  repeatSchedule
                    ? {
                        value: repeatSchedule.numbers[0],
                        label: numberToOrdinal(repeatSchedule.numbers[0]),
                      }
                    : null
                }
                onChange={opt =>
                  handleChange({ repeatFrequency, repeatSchedule: { numbers: [opt.value] } })
                }
              />
            )}
          </div>
        )}
      >
        {({ setIsOpen, isOpen }) => (
          <BaseButton
            {...(compact ? smallControlButtonProps : controlButtonProps)}
            renderLeft={() => <Icon icon={icons.refresh} />}
            onClick={() => setIsOpen(!isOpen)}
          >
            {repeatFrequency && repeatFrequency in frequency
              ? repeatScheduleToText({ repeatFrequency, repeatSchedule })
              : "Repeat..."}
          </BaseButton>
        )}
      </InteractivePopout>
    </div>
  )
}

export function CreateTaskForResourceButton({
  model,
  recordId,
}: {
  model: ITaskResourceModels
  recordId: string
}) {
  return (
    <InteractivePopout
      canCloseOnClickOutside={false}
      maxWidth={700}
      content={({ setIsOpen }) => (
        <div
          css={css`
            width: 700px;
          `}
        >
          <NewTaskForm
            model={model}
            recordId={recordId}
            afterCreate={() => setIsOpen(false)}
            onCancel={() => setIsOpen(false)}
            autofocus
          />
        </div>
      )}
    >
      {({ setIsOpen, isOpen }) => (
        <BaseButton
          theme={{
            fontSize: 13,
          }}
          onClick={() => setIsOpen(!isOpen)}
          renderLeft={() => <Icon icon={icons.task} />}
          buttonStyle="text"
        >
          Create task
        </BaseButton>
      )}
    </InteractivePopout>
  )
}

export function TasksSummaryPopup() {
  const { currentUser } = useGlobalState()
  const skeem = window.skeem
  const [tasks, setTasks] = useState<ITask[] | null>(null)
  const [overdueOpen, setOverdueOpen] = useState(false)
  const [canCloseOnClickOutside, setCanCloseOnClickOutside] = useState(true)
  const tasksLoaded = tasks !== null

  useAsyncEffect(async () => {
    await fetchTasks()
  }, [])

  useEffect(() => {
    if (!tasksLoaded) {
      return
    }
    if (tasks.some(taskIsOverdue)) {
      setOverdueOpen(true)
    }
  }, [tasksLoaded])

  async function fetchTasks() {
    const tasks = await skeem.fetch2<ITask[]>({
      model: {
        name: "tasks",
        attributes: [...basicTaskAttrs, { name: "assigned_to", attributes: basicAssignedToAttrs }],
        filter: {
          and: [
            {
              or: [
                { matches: { assigned_to: { some: { id: currentUser.id } } } },
                { matches: { created_by: currentUser.id } },
              ],
            },
            { matches: { status: "open" } },
          ],
        },
        sort: { by: "due_at", direction: "desc" },
      },
    })
    setTasks(tasks)
  }

  function taskIsOverdue(task: ITask) {
    // task is open
    if (task.status !== taskStatuses.open) {
      return false
    }
    // user is assigned to task
    if (!task.assigned_to.some(user => user.id === currentUser.id)) {
      return false
    }
    // task has a due date
    if (!task.due_at) {
      return false
    }
    // task has past due date
    return new Date(task.due_at) < new Date("2024-11-01")
  }

  return (
    <React.Fragment>
      <InteractivePopout
        canCloseOnClickOutside={canCloseOnClickOutside}
        content={() => (
          <div>
            <div
              css={css`
                display: flex;
                justify-content: space-between;
                background-color: ${brandColors.night};
                padding: 12px;
                border-radius: 4px 4px 0 0;
              `}
            >
              <H4 color="white" weight={400}>
                Tasks
              </H4>
            </div>
            {tasks && tasks.length === 0 && (
              <Grid columns={[40, { fr: 1 }, 40]} gap={12}>
                <div />
                <div
                  css={css`
                    padding: 16px;
                    width: 100%;
                  `}
                >
                  <H3 weight={500}>You have no tasks.</H3>
                </div>
                <div />
              </Grid>
            )}
            <div
              css={css`
                padding: 4px;
                overflow-y: auto;
                overflow-x: hidden;
                max-height: 400px;
              `}
            >
              <Grid gap={4}>
                {tasks &&
                  tasks.map(task => (
                    <TaskListItem
                      key={task.id}
                      task={task}
                      onUpdate={() => fetchTasks()}
                      setParentCanClose={setCanCloseOnClickOutside}
                      css={css`
                        min-width: 342px;
                      `}
                      compact
                    />
                  ))}
              </Grid>
            </div>
            <div
              css={css`
                background-color: ${themeColors.lightGrey};
                border-radius: 0 0 4px 4px;
              `}
            >
              <LinkButton to={eRoutes["/crm/tasks"]} leftIcon={icons.read} buttonStyle="text">
                View all tasks
              </LinkButton>
            </div>
          </div>
        )}
      >
        {({ setIsOpen, isOpen }) => (
          <div
            css={css`
              position: relative;
            `}
          >
            {tasks && (
              <PollForTasks
                initialTasksCount={tasks.length}
                onNewTasks={openPopout => {
                  if (openPopout) {
                    setIsOpen(true)
                  }
                  fetchTasks()
                }}
              />
            )}
            <IconButton
              onClick={() => setIsOpen(!isOpen)}
              icon={icons.tasks}
              color={"white"}
              buttonStyle={"text"}
              square
            />
            <div
              css={css`
                position: absolute;
                z-index: 2;
                bottom: 4px;
                pointer-events: none;
                right: 4px;
              `}
            >
              <Badge count={tasks?.length} />
            </div>
          </div>
        )}
      </InteractivePopout>
      {tasks && (
        <Modal
          render={() => (
            <div>
              <TextGroupMedium
                heading={`You have ${tasks.length} overdue task${tasks.length === 1 ? "" : "s"}`}
              />
              <Spacer />
              <div
                css={css`
                  max-height: 500px;
                  overflow-y: auto;
                  overflow-x: hidden;
                  padding: 4px;
                `}
              >
                <Grid gap={8}>
                  {tasks.filter(taskIsOverdue).map(task => (
                    <TaskListItem
                      key={task.id}
                      task={task}
                      onUpdate={() => fetchTasks()}
                      // modals are at 103
                      popOutZIndex={104}
                    />
                  ))}
                </Grid>
              </div>
              <Spacer />
              <ButtonsWrapper>
                <LinkButton to={eRoutes["/crm/tasks"]} onClick={() => setOverdueOpen(false)}>
                  View all tasks
                </LinkButton>
                <Button
                  onClick={() => setOverdueOpen(false)}
                  buttonColor="white"
                  leftIcon={icons.cross}
                >
                  Close
                </Button>
              </ButtonsWrapper>
            </div>
          )}
          isOpen={overdueOpen}
        />
      )}
    </React.Fragment>
  )
}

function PollForTasks({
  initialTasksCount,
  onNewTasks,
}: {
  initialTasksCount: number
  onNewTasks(shouldOpenPopout: boolean): void
}) {
  const skeem = window.skeem
  const [taskCount, setTaskCount] = useState(initialTasksCount)
  const [notOwnTaskCount, setNotOwnTaskCount] = useState(initialTasksCount)
  const { currentUser } = useGlobalState()

  useInterval(async () => {
    await fetchTaskCounts()
  }, 60 * 10 * 1000)

  useAsyncEffect(async () => {
    await fetchTaskCounts()
  }, [])

  async function fetchTaskCounts() {
    const tasks = await skeem.fetch2<{ countNew: number; countNotSelf: number }>({
      obj: {
        countNew: {
          count: {
            name: "tasks",
            filter: {
              and: [
                {
                  or: [
                    { matches: { assigned_to: { some: { id: currentUser.id } } } },
                    { matches: { created_by: currentUser.id } },
                  ],
                },
                { matches: { status: "open" } },
              ],
            },
          },
        },
        countNotSelf: {
          count: {
            name: "tasks",
            filter: {
              and: [
                { matches: { assigned_to: { some: { id: currentUser.id } } } },
                { not: { matches: { created_by: currentUser.id } } },
                { matches: { status: "open" } },
              ],
            },
          },
        },
      },
    })
    const count = tasks.countNew
    const notOwnTasks = tasks.countNotSelf
    if (count !== taskCount) {
      setTaskCount(count)
      setNotOwnTaskCount(notOwnTasks)
      onNewTasks(notOwnTasks > notOwnTaskCount)
    }
  }

  return <React.Fragment />
}

export function RenderTaskOnResource({
  model,
  recordId,
}: {
  model: ITaskResourceModels
  recordId: string
}) {
  const skeem = window.skeem
  const [tasks, setTasks] = useState<ITask[] | null>(null)
  const [focusedTaskId, setFocusedTaskId] = useState<string>("")

  useAsyncEffect(async () => {
    await fetchTask()
  }, [model, recordId])

  async function fetchTask() {
    const tasks = await skeem.fetch2<ITask[]>({
      model: {
        name: "tasks",
        attributes: [...basicTaskAttrs, { name: "assigned_to", attributes: basicAssignedToAttrs }],
        filter: {
          matches: {
            model,
            record_id: recordId,
          },
        },
      },
    })
    setTasks(tasks)
  }

  if (!tasks) {
    return null
  }

  function toggleFocus(taskId: string) {
    setFocusedTaskId(oldTaskId => (oldTaskId === taskId ? "" : taskId))
  }

  return (
    <React.Fragment>
      <PortalTaskBar
        taskId={focusedTaskId}
        handleClose={() => setFocusedTaskId("")}
        onUpdate={data => {
          setTasks(old => old?.map(x => (x.id === data.id ? data : x)) || null)
        }}
      />
      <PollForTasks
        initialTasksCount={tasks.length ?? 0}
        onNewTasks={() => {
          fetchTask()
        }}
      />
      <Grid gap={8}>
        {tasks.map(task => (
          <TaskListItem
            key={task.id}
            task={task}
            onUpdate={() => fetchTask()}
            onClick={() => toggleFocus(task.id)}
          />
        ))}
      </Grid>
    </React.Fragment>
  )
}

function PortalTaskBar({
  taskId,
  handleClose,
  onUpdate,
}: {
  taskId: string
  handleClose: () => void
  onUpdate: (data: ITask) => void
}) {
  if (!taskId) {
    return null
  }
  const target = document.getElementById(APP_LEVEL_TASK_BAR)
  if (target) {
    return createPortal(
      <div
        css={css`
          background-color: ${paneBackground};
          padding: 16px;
          box-shadow: 1px 1px 3px rgba(0, 0, 0, 0.5);
          min-width: 200px;
        `}
      >
        <div
          css={css`
            display: flex;
            justify-content: space-between;
            align-items: center;
          `}
        >
          <H4 blockSpacing={0} size="large">
            Manage Task
          </H4>
          <IconButton icon={icons.x} buttonColor="white" onClick={() => handleClose()} />
        </div>
        <Spacer />

        <RenderTaskPane taskId={taskId} onUpdateData={onUpdate} />

        <Spacer />
        <Button leftIcon={icons.x} buttonColor="white" onClick={() => handleClose()}>
          Close
        </Button>
      </div>,
      target,
    )
  } else {
    console.error("Error generating portal", APP_LEVEL_TASK_BAR)
  }
  return null
}

export function RenderTaskForUser({
  userId,
  filterBy,
  dueWithinDays,
  filterStatus,
}: {
  userId: string
  filterBy?: ITaskResourceModels
  dueWithinDays?: number
  filterStatus?: keyof typeof taskStatuses
}) {
  const skeem = window.skeem
  const [tasks, setTasks] = useState<ITask[] | null>(null)

  useAsyncEffect(async () => {
    await fetchTask()
  }, [dueWithinDays, userId, filterBy])

  async function fetchTask() {
    const tasks = await skeem.fetch2<ITask[]>({
      model: {
        name: "tasks",
        attributes: [...basicTaskAttrs, { name: "assigned_to", attributes: basicAssignedToAttrs }],
        sort: { by: "due_at", direction: "asc" },
        filter: {
          and: [
            {
              exists: {
                path: [{ name: "assigned_to", filter: { matches: { id: userId } } }],
              },
            },
            filterStatus && { matches: { status: filterStatus } },
            filterBy && { matches: { model: filterBy } },
            typeof dueWithinDays === "number" && {
              matches: {
                due_at: { lte: endOfDay(add(new Date(), { days: dueWithinDays })) },
              },
            },
            typeof dueWithinDays === "number" && {
              matches: { due_at: { gte: startOfDay(new Date()) } },
            },
          ].filter(Boolean),
        },
      },
    })
    setTasks(tasks)
  }

  if (!tasks) {
    return null
  }

  return (
    <React.Fragment>
      <PollForTasks
        initialTasksCount={tasks.length ?? 0}
        onNewTasks={() => {
          fetchTask()
        }}
      />
      <Grid gap={8}>
        {tasks.map(task => (
          <TaskListItem key={task.id} task={task} onUpdate={() => fetchTask()} />
        ))}
      </Grid>
    </React.Fragment>
  )
}

export function TaskTableItem({
  model,
  recordId,
}: {
  model: ITaskResourceModels
  recordId: string
}) {
  const skeem = window.skeem
  const [tasks, setTasks] = useState<ITask[] | null>(null)
  useAsyncEffect(async () => {
    await fetchTask()
  }, [model, recordId])

  async function fetchTask() {
    const tasks = await skeem.fetch2<ITask[]>({
      model: {
        name: "tasks",
        attributes: [...basicTaskAttrs, { name: "assigned_to", attributes: basicAssignedToAttrs }],
        filter: {
          matches: {
            model,
            record_id: recordId,
          },
        },
      },
    })
    setTasks(tasks)
  }

  if (!tasks || tasks.length === 0) {
    return null
  }

  return <div>{pluralize(tasks.length, "Task")} outstanding</div>
}

const taskCardStyles = (isFocused?: boolean, isOverdue?: boolean) => css`
  background-color: ${isFocused
    ? brandColors.blueLightBackground
    : isOverdue
    ? brandColors.orangeLightBackground
    : "#fff"};
  transition: 0.3s;
  cursor: pointer;
  border-radius: 4px;

  &:hover {
    background-color: ${isFocused ? brandColors.blueLightBackground : "#f1f1f1"};
  }
`

// Display list of three names with a "and 2 others" at the end if there are more than three
function nameList(names: string[], max = 3) {
  return names.length > max
    ? `${names.slice(0, max).join(", ")} and ${names.length - max} others`
    : names.join(", ")
}

function dayNumberToDayOfWeek(dayNumber: number) {
  return ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"][
    dayNumber - 1
  ] as IDaysOfWeek
}

function arrayOfConsecutiveNumbers(start: number, end: number) {
  return Array.from({ length: end - start + 1 }, (_, i) => i + start)
}

function numberToOrdinal(number: number) {
  const suffix = ["th", "st", "nd", "rd"]
  const v = number % 100
  return number + (suffix[(v - 20) % 10] || suffix[v] || suffix[0])
}

// Remove null values from object
function removeNulls(obj: any) {
  const newObj = { ...obj }
  Object.keys(newObj).forEach(key => {
    if (newObj[key] === null) {
      delete newObj[key]
    }
  })
  return newObj
}

function repeatScheduleToText(repeatOpts: {
  repeatFrequency?: IRepeatFrequency
  repeatSchedule?: IRepeatSchedule
}) {
  const { repeatFrequency, repeatSchedule } = repeatOpts
  if (!repeatFrequency) {
    return ""
  }

  if (repeatFrequency === "daily") {
    return "Repeats daily"
  }

  if (repeatFrequency === "weekly") {
    if (repeatSchedule) {
      return `Repeats every ${dayNumberToDayOfWeek(repeatSchedule.numbers[0])}`
    } else {
      return "Repeats weekly"
    }
  }

  if (repeatFrequency === "monthly") {
    if (repeatSchedule) {
      return `Repeats on the ${numberToOrdinal(repeatSchedule.numbers[0])}`
    } else {
      return "Repeats monthly"
    }
  }
}

const paneBackground = "#FAF9F8"

const smallControlButtonProps = {
  theme: {
    padding: 8,
    buttonColor: "white",
    fontSize: 13,
    color: "#555",
    hoverColor: "#f5f5f5",
  },
}
const controlButtonProps = {
  theme: {
    padding: 14,
    buttonColor: "white",
    fontSize: 14,
    color: "#555",
    hoverColor: "#f5f5f5",
  },
  block: true,
  css: css`
    .centerButton {
      margin: initial;
    }
  `,
  centralButtonClassName: "centerButton",
}
